import { useEffect, useState } from 'react';
import { Icon } from '@nazka/nazka.mapframe.components';
import { useDispatch, useSelector } from 'react-redux';

import { faInfoCircle, faTimes } from '@fortawesome/pro-solid-svg-icons';

import Navbar from '@App/components/Navbar/Navbar';
import DatapanelToggle from '@App/components/DatapanelToggle/DatapanelToggle';
import RiskDataContent from '@App/components/RiskDataContent/RiskDataContent';
import RiskDataInfoComponent from '@App/components/RiskDataInfoComponent/RiskDataInfoComponent';
import LocationSearchControlPortal from '@App/components/LocationSearch/LocationSearchControlPortal';
import MaplibreMap from '@library/MaplibreMap/MaplibreMap';
import TimelineSelector from '@App/components/TimelineSelector/TimelineSelector';
import RiskLegend from '@App/components/RiskLegend/RiskLegend';
import ScreenNotSupported from '@App/components/ScreenNotSupported/ScreenNotSupported';

import { setRiskDatapanelExpanded } from '@library/GuidedTour/guided-tour.slice';
import { usePrev } from '@services/hooks.service';

import { HORIZONS, SCENARIOS } from './risk-data-page.constants';
import { RISK_DATA_REGIONS } from './risk-data';

import './risk-data-page.styl'

function RiskDataPage() {
  const dispatch = useDispatch();

  const [map, setMap] = useState();
  const [mapResizeId] = useState();
  const datapanelExpanded = useSelector(state => state.guidedTour.riskDatapanelExpanded);
  const isTourGuideOpen = useSelector(state => state.guidedTour.isTourGuideOpen);
  const [selectedRegion, setSelectedRegion] = useState('Kasungu');
  const [selectedHorizon, setSelectedHorizon] = useState(HORIZONS[0]);
  const [selectedScenario, setSelectedScenario] = useState(SCENARIOS[0]);
  const [selectedRisk, setSelectedRisk] = useState();
  const [showInfoPage, setShowInfoPage] = useState(false);

  const prevSelectedRegion = usePrev(selectedRegion);
  const toggleDatapanel = () => dispatch(setRiskDatapanelExpanded(!datapanelExpanded));

  useEffect(() => {
    function onMouseClick(e) {
      if (!e.features.length) {
        return;
      }

      const { id } = e.features[0];

      if (id !== 'Likoma') {
        setSelectedRegion(id);
      }
    }

    if (map) {
      map.on('click', 'malawi-regions-fill', onMouseClick);
    }

    return () => {
      if (map) {
        map.off('click', 'malawi-regions-fill', onMouseClick);
      }
    }
  }, [map]);

  useEffect(() => {
    if (map) {
      if (prevSelectedRegion) {
        map.setFeatureState(
          { source: 'boundaries-regions', id: prevSelectedRegion },
          { selected: false },
        );
      }

      if (selectedRegion) {
        map.setFeatureState(
          { source: 'boundaries-regions', id: selectedRegion },
          { selected: true },
        );
      }
    }
  }, [map, prevSelectedRegion, selectedRegion]);

  useEffect(() => {
    if (map) {
      map.setLayoutProperty('kasungu-outline', 'visibility', 'none');
    }
  }, [map]);
  
  useEffect(() => {
    if (isTourGuideOpen) {
      setSelectedRegion('Kasungu');
    }
  }, [isTourGuideOpen]);

  const riskDataForSelectedRegion = RISK_DATA_REGIONS[selectedRegion]?.[selectedHorizon?.id]?.[selectedScenario?.id];

  if (selectedRisk === 'extreme-wind' && selectedHorizon === HORIZONS[2]) {
    setSelectedHorizon(HORIZONS[0]);
  }

  return (
    <div className="risk-data">
      <ScreenNotSupported />
      <Navbar />
      <div className={`risk-data__wrapper ${datapanelExpanded ? 'risk-data__wrapper--expanded' : 'risk-data__wrapper--collapsed'}`}>
        <div className={`risk-data__datapanel my-second-risk-step ${datapanelExpanded ? 'risk-data__datapanel--expanded' : 'inset'} ${isTourGuideOpen ? 'risk-data__datapanel--no-transition' : ''}`}>
          <DatapanelToggle
            expanded={datapanelExpanded}
            onToggle={toggleDatapanel}
            variant="mobile"
            className="risk-data__datapanel-toggle my-eleventh-risk-step-mobile"
            buttonClassName="risk-data__datapanel-toggle__mobile"
          />
          {showInfoPage ? (
            <RiskDataInfoComponent />
          ) : (
            <RiskDataContent
              map={map}
              onSelectedScenarioChange={setSelectedScenario}
              riskData={riskDataForSelectedRegion}
              selectedHorizon={selectedHorizon}
              selectedRegion={selectedRegion}
              selectedRisk={selectedRisk}
              selectedScenario={selectedScenario}
              setSelectedRegion={setSelectedRegion}
              setSelectedRisk={setSelectedRisk}
            />
          )}
          <button
            type="button"
            className="risk-data__info-page-toggle"
            onClick={() => setShowInfoPage(prevState => !prevState)}
          >
            <Icon icon={showInfoPage ? faTimes : faInfoCircle} size="20px" />
          </button>
        </div>
        <div className="risk-data__map my-seventh-risk-step">
          <DatapanelToggle
            expanded={datapanelExpanded}
            onToggle={toggleDatapanel}
            variant="desktop"
            className="risk-data__datapanel-toggle__desktop my-eleventh-risk-step-desktop"
          />
          <div />
          <MaplibreMap
            mapResizeId={mapResizeId}
            onMapInitialized={setMap}
            initialBounds={[[32.671637, -17.129748], [35.918475, -9.367346]]}
          />
          <LocationSearchControlPortal map={map} />
          <div className="risk-data__map__overlay my-eighth-risk-step">
            {selectedRisk && (
              <RiskLegend selectedRisk={selectedRisk} />
            )}
            <TimelineSelector
              options={HORIZONS}
              selectedOption={selectedHorizon}
              onChange={setSelectedHorizon}
              disabledIndex={selectedRisk === 'extreme-wind' ? 2 : undefined}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default RiskDataPage
